import React, { useEffect, useState, useRef, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ReactTags } from 'react-tag-autocomplete';
import styled from 'styled-components';
import IconTag from './icon-tag';
import Spinner from '../spinner/spinner';
import UserService from '../../../services/user-service';
import ToastService from '../../../services/toast-service';
import { captureError } from '~/modules/pixwel/exception-handler.factory';

// eslint-disable-next-line max-len, no-useless-escape
const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// MongoDB ObjectId (24 hex chars). Some recipients are returned by the API as
// bare id strings (e.g. anonymous/unpopulated users). We resolve those to
// friendly labels instead of showing the raw id (PLATFORM-4020).
const MONGO_ID_REGEX = /^[a-f0-9]{24}$/i;
const isBareUserId = (contact) => typeof contact === 'string' && MONGO_ID_REGEX.test(contact);

// A contact copied out of a mail client carries its wrapper — "<john@x.com>",
// "(mailto:john@x.com)", "john@x.com." — so unwrap a pasted token before testing
// it, and drop whatever is not an address once unwrapped (PLATFORM-4200).
// Quotes, brackets and apostrophes are legal in an address ("alice"@x.com,
// alice@[192.0.2.1], 'alice@x.com), so the wrapper comes off one layer at a time
// and the token is re-tested after each: the first form that is an address wins,
// and a legal character next to the wrapper is never stripped with it —
// <'alice@x.com> must not resolve the different person alice@x.com. A matching
// pair around the whole token is one layer, so the "]" of alice@[192.0.2.1]
// is not mistaken for the end of a wrapper.
const WRAPPER_PAIRS = {
	'<': '>', '(': ')', '[': ']', '{': '}', '"': '"', '\'': '\''
};
const TRAILING_PUNCTUATION = /[.:;,]$/;
const WRAPPER_END = /[>)\]}"']$/;
const WRAPPER_START = /^[<([{"']/;
const unwrapOnce = (candidate) => {
	if( candidate.startsWith('mailto:') ) {
		return candidate.slice('mailto:'.length);
	}
	if( candidate.length > 1 && WRAPPER_PAIRS[candidate[0]] === candidate[candidate.length - 1] ) {
		return candidate.slice(1, -1);
	}
	if( TRAILING_PUNCTUATION.test(candidate) || WRAPPER_END.test(candidate) ) {
		return candidate.slice(0, -1);
	}
	if( WRAPPER_START.test(candidate) ) {
		return candidate.slice(1);
	}
	return null;
};
const extractEmail = (token) => {
	let candidate = token;

	while( candidate ) {
		if( EMAIL_REGEX.test(candidate) ) {
			return candidate;
		}
		candidate = unwrapOnce(candidate);
	}

	return null;
};

// errors.
const INVALID_EMAIL = Symbol('invalid-email');
const DUPLICATE_CONTACT = Symbol('duplicate-contact');
const NOT_ALLOWED = Symbol('not-allowed');

const Container = styled.div.attrs({
	'data-cy': 'contact-picker'
})`
	position: relative;

	.react-tags {
		padding-right: 2.75rem;
		border-color: ${({ $isInvalid }) => $isInvalid ? 'rgba(237,85,59,0.67)' : '#767778'};
	}
`;

const SpinnerContainer = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0.75rem;
	display: flex;
	align-items: center;
	opacity: 0.75;
`;

const ContactPicker = (props) => {

	const {
		onUpdate,
		exclude = [],
		allowUsers = true,
		allowGroups = false,
		excludeGroups = [],
		contacts: rawContacts,
		limit,
		isInvalid = false,
		placeholder = '',
		allowEmails = true,
		allowNew = true,
		disabled = false,
		id,
		labelText = ''
	} = props;

	const contacts = useMemo(() => rawContacts?.filter(Boolean) ?? [], [rawContacts]);

	const [suggestions, setSuggestions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [inputSearch, setInputSearch] = useState('');
	const [resolvedUsers, setResolvedUsers] = useState({});
	const isSelectingSuggestion = useRef(false);
	const searchSequence = useRef(0);
	const latestInputSearch = useRef('');

	const isAtLimit = !!limit && contacts.length >= limit;

	const handleAddition = (tag) => {
		if(disabled) {
			return false;
		}

		let { contact } = tag;

		if( !contact ) {
			contact = tag.value;
		}

		const isValid = handleValidate(tag.value);
		if( !isValid ) {
			return false;
		}

		let updated = [
			...contacts,
			contact
		];

		if( limit ) {
			updated = updated.slice(0, limit);
		}

		setSuggestions(suggestions.filter((s) => s.value !== contact));
		onUpdate(updated);
		return true;
	};

	const handleDelete = (tag) => {
		if(disabled) {
			return;
		}
		const updated = contacts.filter((c) => typeof c === 'string' ? c !== tag : (c.id ?? c._id) !== (tag.id ?? tag._id));
		onUpdate(updated);
	};

	/**
	 * When the input search changes, search for contacts and update the suggestions
	 */
	useEffect(() => {
		if( inputSearch.length ) {
			searchSequence.current += 1;
			const requestId = searchSequence.current;
			setLoading(true);
			searchContacts(inputSearch, requestId);
		}
	}, [inputSearch]);

	useEffect(() => {
		if( isAtLimit ) {
			searchSequence.current += 1;
			latestInputSearch.current = '';
			setInputSearch('');
			setSuggestions([]);
			setLoading(false);
		}
	}, [isAtLimit]);

	// Resolve any bare user-id strings in `contacts` to full user objects so we
	// can display a friendly name/email instead of the raw MongoDB id
	// (PLATFORM-4020). Only run when users are allowed; in group-only mode bare
	// 24-char strings are group ids and must not be queried as users.
	useEffect(() => {
		if( !allowUsers ) {
			return;
		}

		const unresolvedIds = contacts
			.filter(isBareUserId)
			.filter((id) => !(id in resolvedUsers));

		if( unresolvedIds.length === 0 ) {
			return;
		}

		const uniqueIds = [...new Set(unresolvedIds)];

		let cancelled = false;
		UserService.getUsersByIds(uniqueIds)
			.then((res) => {
				if( cancelled ) {
					return;
				}
				const fetched = res?.users?.nodes ?? [];
				setResolvedUsers((prev) => {
					const next = { ...prev };

					// Cache requested ids even when not found to avoid refetch loops.
					for( const id of uniqueIds ) {
						if( !(id in next) ) {
							next[id] = null;
						}
					}

					for( const user of fetched ) {
						if( user?.id ) {
							next[user.id] = user;
						}
					}
					return next;
				});
			})
			.catch((err) => {
				captureError?.(err);
			});

		return () => {
			cancelled = true;
		};
	}, [contacts, resolvedUsers, allowUsers]);

	const searchContacts = async(input, requestId) => {
		try {
			let tasks = [];
			if( allowUsers ) {
				tasks.push(UserService.getUsers(input));
			}
			if( allowGroups ) {
				tasks.push(UserService.getGroups(input));
			}

			const results = await Promise.all(tasks);

			const { users, groups } = results.reduce((acc, res) => {
				const key = Object.keys(res)[0];
				return {
					...acc,
					[key]: res[key]
				};
			}, { users: { nodes: [] }, groups: { nodes: [] } });

			// Convert structures to a single contact structure
			let suggestions = [
				...(users.nodes?.map((user) => ({
					label: user.name ? `${user.name}\n${user.emailNormal}` : user.emailNormal,
					value: user
				})) ?? []),
				...(groups.nodes?.map((group) => ({
					label: group.name,
					value: group
				})) ?? [])
			].slice(0, 6);

			if( shouldIgnoreSearchResults(requestId, input) ) {
				return;
			}

			setSuggestions(suggestions);
		}
		catch(e) {
			captureError(e, 'Error searching contacts');
		}
		finally {
			if( searchSequence.current === requestId ) {
				setLoading(false);
			}
		}
	};

	const handleInputChange = _.debounce(async(input) => {

		// If the length is not long enough, clear any prior suggestions so the
		// dropdown doesn't show stale options before the user has typed enough
		// to trigger a new search. See PLATFORM-3979.
		const value = input.trim();
		if( value.length < 3 ) {
			searchSequence.current += 1;
			latestInputSearch.current = '';
			setInputSearch('');
			setSuggestions([]);
			setLoading(false);
			return;
		}

		// If the input is the same as the last input, ignore this event
		if( value === inputSearch ) {
			return;
		}

		latestInputSearch.current = value;
		setInputSearch(value);
	}, 250);

	function shouldIgnoreSearchResults(requestId, input) {
		return searchSequence.current !== requestId
			|| latestInputSearch.current !== input
			|| isAtLimit;
	}

	const handlePaste = async(e) => {
		let pastedText = (e.clipboardData || window.clipboardData).getData('text');

		// Prevent default paste behavior
		e.stopPropagation();
		e.preventDefault();

		setLoading(true);

		// Split pasted contacts on supported delimiters and keep only the tokens
		// that are an email once unwrapped. A contact copied out of a mail client
		// arrives as a whole line — "Hansard, John - Paramount john@x.com
		// (mailto:john@x.com)" — and every one of those words used to be sent to
		// the user search below, which is a fuzzy `q` match, so "john" and
		// "paramount" each resolved to an unrelated person who was then added as
		// a recipient (PLATFORM-4200).
		const pastedEmails = _.uniq(
			pastedText
				.split(/[\s,;|]+/)
				.map((c) => extractEmail(c.toLowerCase().trim()))
				.filter(Boolean)
		);

		if( !pastedEmails.length ) {
			handleError({
				type: INVALID_EMAIL,
				contact: _.truncate(pastedText.trim(), { length: 100 })
			});
			setLoading(false);
			return;
		}

		// Convert pasted emails into User objects before type validation. Only a
		// hit that carries the pasted address itself is trusted — `users(q:)`
		// matches on name too, so the first node is not necessarily this person.
		// Anything else falls back to the pasted email, same as an unknown one.
		const convertedContacts = await Promise.all(pastedEmails.map(async(email) => {
			const { users: { nodes } } = await UserService.getUsers(email);
			const user = nodes?.find((node) => node?.emailNormal?.toLowerCase() === email);

			return user
				? { label: email, value: { ...user } }
				: { label: email, value: email };
		}));

		let validContacts = convertedContacts.filter(handleValidate).map((c) => {
			return c.value || c.label;
		});

		// Add valid contacts
		if( validContacts.length ) {
			let updated = [
				...contacts,
				...validContacts
			];

			if( limit ) {
				updated = updated.slice(0, limit);
			}

			onUpdate(updated);
		}

		setLoading(false);
	};

	const handleValidate = (_contact) => {
		// User from dropdown : { id, name, emailNormal }
		// Paste known user : { label, value: { id, name, emailNormal } }
		// Paste unknown email : { label, value: 'email' }
		// Group : { id, name }

		if( !_contact ) {
			return false;
		}
		const contact = _contact.value ?? _contact;

		// Reject whitespace-only strings and objects that carry no usable
		// identity — defensive guard to prevent malformed contacts from
		// being added as recipients.
		if( typeof contact === 'string' && !contact.trim() ) {
			return false;
		}
		if( typeof contact === 'object' && !contact.id && !contact._id
			&& !contact.emailNormal && !contact.email && !contact.name ) {
			return false;
		}

		// Block additions while any bare-id user lookup is still in flight to
		// prevent accidentally adding a duplicate email for an unresolved
		// recipient (PLATFORM-4020).
		if( allowUsers && contacts.some((c) => isBareUserId(c) && !(c in resolvedUsers)) ) {
			return false;
		}

		const isEmail = _.isString(contact) && !contact?.id;
		const isUser = !!contact?.id && !!contact?.emailNormal;
		const isGroup = !!contact?.id && !!contact?.name;
		if( !isEmail && !isUser && !isGroup ) {
			return false;
		}

		let error = null;
		if( isEmail ) {
			error = validateEmail(contact);
			if( error ) {
				handleError(error);
			}
		}
		else if( isUser ) {
			error = validateUser(contact);
			if( error ) {
				handleError(error);
			}
		}
		else if( isGroup ) {
			error = validateGroup(contact);
			if( error ) {
				handleError(error);
			}
		}

		return !error;
	};

	const validateEmail = (email) => {
		// Make sure emails are allowed
		if( !allowEmails ) {
			return {
				type: NOT_ALLOWED,
				contact: email
			};
		}

		// Make sure email is a valid email
		let isValidEmail = EMAIL_REGEX.test(email.toLowerCase());
		if( !isValidEmail ) {
			return {
				type: INVALID_EMAIL,
				contact: email
			};
		}

		// Make sure email isn't on list of contacts already
		let isUnique = contacts.every((contact) => {
			if( typeof contact === 'string' ) {
				if( contact.toLowerCase() === email.toLowerCase() ) {
					return false;
				}

				const resolvedContact = isBareUserId(contact) ? resolvedUsers[contact] : null;
				return !resolvedContact?.emailNormal
					|| resolvedContact.emailNormal.toLowerCase() !== email.toLowerCase();
			}

			return !contact.emailNormal || contact.emailNormal.toLowerCase() !== email.toLowerCase();
		});
		if( !isUnique ) {
			return {
				type: DUPLICATE_CONTACT,
				contact: email
			};
		}

		// Make sure email is not on list of excluded users
		let isExcludedEmail = !exclude.every((excludedUser) => {
			if( typeof excludedUser === 'string' ) {
				return excludedUser.toLowerCase() !== email.toLowerCase();
			}

			return !excludedUser.emailNormal || excludedUser.emailNormal.toLowerCase() !== email.toLowerCase();
		});
		if( isExcludedEmail ) {
			return {
				type: NOT_ALLOWED,
				contact: email
			};
		}
	};

	const validateUser = (user) => {

		// Make sure user selections are allowed
		if( !allowUsers ) {
			return {
				type: NOT_ALLOWED,
				contact: user
			};
		}

		// Make sure user isn't already in contacts
		let isUnique = contacts.every((contact) => {
			if( typeof contact === 'string' ) {
				if( contact === user.emailNormal || contact === user.id ) {
					return false;
				}

				const resolvedContact = isBareUserId(contact) ? resolvedUsers[contact] : null;
				return resolvedContact?.id !== user.id && resolvedContact?.emailNormal !== user.emailNormal;
			}

			return contact.emailNormal !== user.emailNormal && contact.id !== user.id;
		});
		if( !isUnique ) {
			return {
				type: DUPLICATE_CONTACT,
				contact: user
			};
		}

		// Make sure user isn't on exclude list
		let isExcludedUser = !exclude.every((excludedContact) => {
			if( typeof excludedContact === 'string' ) {
				return excludedContact !== user.emailNormal
					&& excludedContact !== user.id;
			}

			return excludedContact.emailNormal !== user.emailNormal
				&& excludedContact.id !== user.id;
		});

		if( isExcludedUser ) {
			return {
				type: NOT_ALLOWED,
				contact: user
			};
		}
	};

	const validateGroup = (group) => {

		// Make sure group selections are allowed
		if( !allowGroups ) {
			return {
				type: NOT_ALLOWED,
				contact: group
			};
		}

		let isUnique = contacts.every((contact) => {
			let isGroup = typeof contact === 'object' && 'users' in contact;
			return !isGroup || contact.id !== group.id;
		});

		if( !isUnique ) {
			return {
				type: DUPLICATE_CONTACT,
				contact: group
			};
		}

		let isExcludedGroup = !excludeGroups.every((excludedGroup) => {
			return excludedGroup.id !== group.id;
		});

		if( isExcludedGroup ) {
			return {
				type: NOT_ALLOWED,
				contact: group
			};
		}
	};

	const handleError = (error) => {
		let { type: errorType, contact: invalidContact } = error;
		let message = `Invalid contact: ${invalidContact}`;

		switch( errorType ) {
			case INVALID_EMAIL:
				message = `Invalid email: ${invalidContact}`;
				break;

			case DUPLICATE_CONTACT:
				message = `Contact already selected: ${
					invalidContact.emailNormal || invalidContact.name || invalidContact
				}`;
				break;

			case NOT_ALLOWED:
				message = `Contact not allowed: ${
					invalidContact.emailNormal || invalidContact.name || invalidContact
				}`;
				break;
		}

		ToastService.create('error', message, {
			timeout: 8000,
			dismissOnClick: true
		});
	};

	function CustomInput({ classNames, inputWidth, ...inputProps }) {
		const handleBlur = (e) => {
			// Skip if the user clicked a suggestion — the suggestion's onAdd handler will commit it
			const relatedTarget = e.relatedTarget instanceof Element ? e.relatedTarget : null;
			if( relatedTarget?.closest('.react-tags__listbox') || isSelectingSuggestion.current ) {
				isSelectingSuggestion.current = false;
				inputProps.onBlur?.(e);
				return;
			}
			const value = e.target.value?.trim();
			if( value && allowNew ) {
				handleAddition({ label: value, value: value });
				e.target.value = '';
				inputProps.onChange?.(e);
			}
			inputProps.onBlur?.(e);
		};

		return <input className={classNames}
			style={{ width: inputWidth }}
			readOnly={disabled || (limit && contacts.length >= limit)}
			onPaste={handlePaste}
			{...inputProps}
			onBlur={handleBlur}
		/>;
	}

	const tags = contacts?.map((contact) => {
		const bareId = allowUsers && isBareUserId(contact);
		const resolved = bareId ? resolvedUsers[contact] : null;

		let isEmail = typeof contact === 'string' && !bareId;
		let isUser = resolved || (!isEmail && typeof contact === 'object' && 'emailNormal' in contact);
		let label;
		let icon;

		if( isEmail ) {
			label = contact;
			icon = 'mail';
		}
		else if( resolved ) {
			label = resolved.name ?? resolved.emailNormal;
			icon = 'user';
		}
		else if( bareId ) {
			label = 'Anonymous recipient';
			icon = 'user';
		}
		else {
			label = contact.name ?? contact.emailNormal;
			icon = isUser ? 'user' : 'group';
		}

		return {
			label,
			value: contact,
			icon
		};
	}) ?? [];

	function CustomOption({ children, classNames, option, ...optionProps }) {
		const classes = [
			classNames.option,
			option.active ? 'is-active' : '',
			option.selected ? 'is-selected' : '',
			option.disabled ? 'is-disabled' : '',
			isInvalid ? 'is-invalid' : ''
		];

		return (
			<div className={classes.join(' ')} {...optionProps}>
				{children}
			</div>
		);
	}

	const visibleSuggestions = isAtLimit ? [] : suggestions;

	return (
		<Container data-testid="contact-picker" $isInvalid={isInvalid} onMouseDown={(e) => {
			const target = e.target instanceof Element ? e.target : null;
			if( target?.closest('.react-tags__listbox') ) {
				isSelectingSuggestion.current = true;
			}
		}}
		>
			<ReactTags
				id={id}
				placeholderText={''}
				labelText={labelText}
				isInvalid={isInvalid}
				selected={tags}
				suggestions={visibleSuggestions}
				suggestionsFilter={() => true}
				onAdd={handleAddition}
				onDelete={handleDelete}
				onInput={handleInputChange}
				// onValidate={handleValidate}
				delimiterKeys={['Enter', 'Tab', ',', ';']}
				renderTag={(props) => <IconTag {...props} onDelete={handleDelete}/>}
				allowNew={allowNew}
				activateFirstOption={true}
				renderInput={CustomInput}
				renderOption={CustomOption}
				placeholder={placeholder}
				noOptionsText={''}
			/>
			{loading && (
				<SpinnerContainer>
					<Spinner size={1.5} />
				</SpinnerContainer>
			)}
		</Container>
	);
};

ContactPicker.propTypes = {
	contacts: PropTypes.array.isRequired,
	onUpdate: PropTypes.func.isRequired,
	exclude: PropTypes.array,
	allowUsers: PropTypes.bool,
	allowGroups: PropTypes.bool,
	allowEmails: PropTypes.bool,
	excludeGroups: PropTypes.array,
	isInvalid: PropTypes.bool,
	allowNew: PropTypes.bool,
	classNames: PropTypes.string,
	inputWidth: PropTypes.string,
	option: PropTypes.object,
	limit: PropTypes.number,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
	// Forwarded to the tag input: `id` becomes `${id}-input` on the input itself,
	// `labelText` its accessible name (ReactTags renders it as the input's label).
	id: PropTypes.string,
	labelText: PropTypes.string
};

export default ContactPicker;
